import { Component, ErrorInfo, ReactNode } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/shared/ui/card';
import { Button } from '@/shared/ui/button';
import { Alert, AlertDescription } from '@/shared/ui/alert';
import { RefreshCw, AlertTriangle, WifiOff } from 'lucide-react';

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
  isOnline: boolean;
  networkError: boolean;
}

export class ErrorBoundaryWithNetwork extends Component<Props, State> {
  private retryCount = 0;
  private maxRetries = 3;

  constructor(props: Props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
      isOnline: navigator.onLine,
      networkError: false
    };
  }

  componentDidMount() {
    // Monitor network status
    window.addEventListener('online', this.handleOnline);
    window.addEventListener('offline', this.handleOffline);
    
    // Monitor for blocked requests
    this.interceptNetworkErrors();
  }

  componentWillUnmount() {
    window.removeEventListener('online', this.handleOnline);
    window.removeEventListener('offline', this.handleOffline);
  }

  handleOnline = () => {
    this.setState({ isOnline: true, networkError: false });
  };

  handleOffline = () => {
    this.setState({ isOnline: false });
  };

  interceptNetworkErrors = () => {
    // Monitor console for blocked requests
    const originalConsoleError = console.error;
    console.error = (...args: any[]) => {
      const errorMessage = args.join(' ');
      if (errorMessage.includes('ERR_BLOCKED_BY_CLIENT') || 
          errorMessage.includes('net::ERR_BLOCKED_BY_CLIENT')) {
        this.setState({ networkError: true });
      }
      originalConsoleError.apply(console, args);
    };
  };

  static getDerivedStateFromError(error: Error): State {
    return {
      hasError: true,
      error,
      errorInfo: null,
      isOnline: navigator.onLine,
      networkError: false
    };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    this.setState({ errorInfo });
    
    // Check if error is network related
    if (error.message.includes('Failed to fetch') || 
        error.message.includes('Network Error') ||
        error.message.includes('ERR_BLOCKED_BY_CLIENT')) {
      this.setState({ networkError: true });
    }
  }

  handleRetry = () => {
    if (this.retryCount < this.maxRetries) {
      this.retryCount++;
      this.setState({ 
        hasError: false, 
        error: null, 
        errorInfo: null,
        networkError: false 
      });
    }
  };

  render() {
    const { hasError, error, networkError } = this.state;
    const { children } = this.props;

    // Network error handling
    if (networkError) {
      return (
        <Card className="max-w-2xl mx-auto mt-8">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-amber-600">
              <WifiOff className="h-5 w-5" />
              Network Request Blocked
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <Alert>
              <AlertTriangle className="h-4 w-4" />
              <AlertDescription>
                Your browser extension or network settings are blocking some requests. This is preventing the application from working properly.
              </AlertDescription>
            </Alert>
            
            <div className="space-y-3">
              <h4 className="font-medium">How to fix this:</h4>
              <ul className="list-disc list-inside space-y-1 text-sm text-muted-foreground">
                <li>Check if you have ad blockers or privacy extensions enabled</li>
                <li>Temporarily disable browser extensions to test</li>
                <li>Add <code className="bg-muted px-1 rounded">lovable.dev</code> to your extension's whitelist</li>
                <li>Try using an incognito/private browsing window</li>
                <li>Check your corporate firewall or VPN settings</li>
              </ul>
            </div>

            <div className="flex gap-2">
              <Button onClick={this.handleRetry} className="flex items-center gap-2">
                <RefreshCw className="h-4 w-4" />
                Try Again
              </Button>
              <Button variant="outline" onClick={() => window.location.reload()}>
                Refresh Page
              </Button>
            </div>
          </CardContent>
        </Card>
      );
    }

    // Note: navigator.onLine check removed — it is unreliable and causes
    // false positives. Real network issues are caught by the networkError
    // state via interceptNetworkErrors().

    // General error handling
    if (hasError && error) {
      return (
        <Card className="max-w-2xl mx-auto mt-8">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-red-600">
              <AlertTriangle className="h-5 w-5" />
              Something went wrong
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <Alert variant="destructive">
              <AlertDescription>
                {error.message || 'An unexpected error occurred'}
              </AlertDescription>
            </Alert>
            
            {this.retryCount < this.maxRetries && (
              <Button onClick={this.handleRetry} className="flex items-center gap-2">
                <RefreshCw className="h-4 w-4" />
                Try Again ({this.maxRetries - this.retryCount} attempts left)
              </Button>
            )}
            
            <details className="text-sm">
              <summary className="cursor-pointer text-muted-foreground">
                Technical Details
              </summary>
              <pre className="mt-2 p-2 bg-muted rounded text-xs overflow-auto">
                {error.stack}
              </pre>
            </details>
          </CardContent>
        </Card>
      );
    }

    return children;
  }
}